import { supabase } from './supabase';

export type AIAgentKind = 'generic' | 'lead';

/** Preset backend goal for lead agents (`ai_agents.lead_goal`). Generic agents keep null. */
export const LEAD_AGENT_GOAL_OPTIONS = [
  {
    id: 'gather_info_sales_call',
    label: 'Gather information for a sales call',
    description:
      'Focus SMS on qualifying the lead per your training, then booking a sales call — not deep pricing interviews.',
  },
  {
    id: 'none',
    label: 'General (no preset)',
    description: 'Only your purpose text and training apply — no extra runtime goal.',
  },
] as const;

export type LeadAgentGoalId = (typeof LEAD_AGENT_GOAL_OPTIONS)[number]['id'];

function normalizeLeadGoal(kind: string | undefined, raw: string | null | undefined): LeadAgentGoalId | null {
  if (kind !== 'lead') return null;
  if (raw === 'none' || raw === 'gather_info_sales_call') return raw;
  return 'gather_info_sales_call';
}

export interface AIAgentToolConfig {
  sms?: { enabled?: boolean };
  [key: string]: unknown;
}

export interface AIAgent {
  id: string;
  business_id: string;
  name: string;
  description: string | null;
  purpose: string | null;
  icon: string;
  status: 'draft' | 'active' | 'archived';
  model: string;
  agent_kind: AIAgentKind;
  /** Lead agents only; null for generic. */
  lead_goal: LeadAgentGoalId | null;
  tool_config: AIAgentToolConfig;
  created_at: string;
  updated_at: string;
  rule_count?: number;
}

export interface AIAgentRule {
  id: string;
  agent_id: string;
  category: 'pricing' | 'surcharges' | 'conditions' | 'items' | 'discounts' | 'general';
  name: string;
  description: string | null;
  condition: string | null;
  value: any;
  value_type: 'currency' | 'currency_per_unit' | 'percentage' | 'boolean' | 'text' | 'list' | 'number';
  unit: string | null;
  is_active: boolean;
  source_message: string | null;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface AIAgentConversation {
  id: string;
  agent_id: string;
  title: string;
  messages: ConversationMessage[];
  created_at: string;
  updated_at: string;
}

export interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  rules_extracted: string[];
  images?: string[];
}

// --- Lists ---

export interface AIAgentListItem {
  label: string;
  value: string;
  notes?: string;
}

export interface AIAgentList {
  id: string;
  agent_id: string;
  title: string;
  items: AIAgentListItem[];
  is_active: boolean;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export async function getAgentLists(agentId: string): Promise<AIAgentList[]> {
  const { data, error } = await supabase
    .from('ai_agent_lists')
    .select('*')
    .eq('agent_id', agentId)
    .order('order_index')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function createAgentList(agentId: string, title: string): Promise<AIAgentList> {
  const { data, error } = await supabase
    .from('ai_agent_lists')
    .insert({ agent_id: agentId, title, items: [] })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateAgentList(listId: string, updates: Partial<Pick<AIAgentList, 'title' | 'items' | 'is_active' | 'order_index'>>): Promise<AIAgentList> {
  const { data, error } = await supabase
    .from('ai_agent_lists')
    .update(updates)
    .eq('id', listId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteAgentList(listId: string): Promise<void> {
  const { error } = await supabase
    .from('ai_agent_lists')
    .delete()
    .eq('id', listId);

  if (error) throw error;
}

// --- Agents ---

export async function getAgents(
  businessId: string,
  opts?: { kind?: AIAgentKind }
): Promise<AIAgent[]> {
  let q = supabase
    .from('ai_agents')
    .select('*, ai_agent_rules(count)')
    .eq('business_id', businessId)
    .neq('status', 'archived')
    .order('created_at', { ascending: false });

  if (opts?.kind) {
    q = q.eq('agent_kind', opts.kind);
  }

  const { data, error } = await q;

  if (error) throw error;

  return (data || []).map((agent: any) => ({
    ...agent,
    agent_kind: (agent.agent_kind || 'generic') as AIAgentKind,
    lead_goal: normalizeLeadGoal(agent.agent_kind, agent.lead_goal),
    tool_config: (agent.tool_config || {}) as AIAgentToolConfig,
    rule_count: agent.ai_agent_rules?.[0]?.count || 0,
    ai_agent_rules: undefined
  }));
}

export async function getAgent(agentId: string): Promise<AIAgent | null> {
  const { data, error } = await supabase
    .from('ai_agents')
    .select('*')
    .eq('id', agentId)
    .single();

  if (error) return null;
  const row = data as any;
  return {
    ...row,
    agent_kind: (row.agent_kind || 'generic') as AIAgentKind,
    lead_goal: normalizeLeadGoal(row.agent_kind, row.lead_goal),
    tool_config: (row.tool_config || {}) as AIAgentToolConfig,
  };
}

export async function createAgent(params: {
  businessId: string;
  name: string;
  purpose?: string;
  icon?: string;
  agentKind?: AIAgentKind;
  /** Required when creating a lead agent (defaults to sales-call intake if omitted). */
  leadGoal?: LeadAgentGoalId | null;
}): Promise<AIAgent> {
  const kind = params.agentKind || 'generic';
  const tool_config: AIAgentToolConfig =
    kind === 'lead' ? { sms: { enabled: false } } : {};
  const lead_goal =
    kind === 'lead' ? (params.leadGoal === 'none' ? 'none' : 'gather_info_sales_call') : null;

  const { data, error } = await supabase
    .from('ai_agents')
    .insert({
      business_id: params.businessId,
      name: params.name,
      purpose: params.purpose || null,
      icon: params.icon || (kind === 'lead' ? '🎯' : '🤖'),
      agent_kind: kind,
      lead_goal,
      tool_config,
    })
    .select()
    .single();

  if (error) throw error;
  const row = data as any;
  return {
    ...row,
    agent_kind: (row.agent_kind || 'generic') as AIAgentKind,
    lead_goal: normalizeLeadGoal(row.agent_kind, row.lead_goal),
    tool_config: (row.tool_config || {}) as AIAgentToolConfig,
  };
}

export async function updateAgent(
  agentId: string,
  updates: Partial<
    Pick<
      AIAgent,
      | 'name'
      | 'description'
      | 'purpose'
      | 'icon'
      | 'status'
      | 'model'
      | 'agent_kind'
      | 'tool_config'
      | 'lead_goal'
    >
  >
): Promise<AIAgent> {
  const { data, error } = await supabase
    .from('ai_agents')
    .update(updates)
    .eq('id', agentId)
    .select()
    .single();

  if (error) throw error;
  const row = data as any;
  return {
    ...row,
    agent_kind: (row.agent_kind || 'generic') as AIAgentKind,
    lead_goal: normalizeLeadGoal(row.agent_kind, row.lead_goal),
    tool_config: (row.tool_config || {}) as AIAgentToolConfig,
  };
}

export async function deleteAgent(agentId: string): Promise<void> {
  const { error } = await supabase
    .from('ai_agents')
    .delete()
    .eq('id', agentId);

  if (error) throw error;
}

// --- Rules ---

export async function getAgentRules(agentId: string): Promise<AIAgentRule[]> {
  const { data, error } = await supabase
    .from('ai_agent_rules')
    .select('*')
    .eq('agent_id', agentId)
    .order('category')
    .order('order_index');

  if (error) throw error;
  return data || [];
}

export async function updateAgentRule(ruleId: string, updates: Partial<Pick<AIAgentRule, 'name' | 'description' | 'condition' | 'value' | 'value_type' | 'unit' | 'is_active' | 'category'>>): Promise<AIAgentRule> {
  const { data, error } = await supabase
    .from('ai_agent_rules')
    .update(updates)
    .eq('id', ruleId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteAgentRule(ruleId: string): Promise<void> {
  const { error } = await supabase
    .from('ai_agent_rules')
    .delete()
    .eq('id', ruleId);

  if (error) throw error;
}

export async function toggleAgentRule(ruleId: string, isActive: boolean): Promise<void> {
  const { error } = await supabase
    .from('ai_agent_rules')
    .update({ is_active: isActive })
    .eq('id', ruleId);

  if (error) throw error;
}

// --- Conversations ---

export async function getAgentConversations(agentId: string): Promise<AIAgentConversation[]> {
  const { data, error } = await supabase
    .from('ai_agent_conversations')
    .select('*')
    .eq('agent_id', agentId)
    .order('updated_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function getConversation(conversationId: string): Promise<AIAgentConversation | null> {
  const { data, error } = await supabase
    .from('ai_agent_conversations')
    .select('*')
    .eq('id', conversationId)
    .single();

  if (error) return null;
  return data;
}

// --- Scenarios ---

export interface AIAgentScenario {
  id: string;
  agent_id: string;
  title: string;
  description: string | null;
  images: string[];
  messages: ConversationMessage[];
  outcome: any;
  tags: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export async function getAgentScenarios(agentId: string): Promise<AIAgentScenario[]> {
  const { data, error } = await supabase
    .from('ai_agent_scenarios')
    .select('*')
    .eq('agent_id', agentId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function getScenario(scenarioId: string): Promise<AIAgentScenario | null> {
  const { data, error } = await supabase
    .from('ai_agent_scenarios')
    .select('*')
    .eq('id', scenarioId)
    .single();

  if (error) return null;
  return data;
}

export async function deleteScenario(scenarioId: string): Promise<void> {
  const { error } = await supabase
    .from('ai_agent_scenarios')
    .delete()
    .eq('id', scenarioId);

  if (error) throw error;
}

export async function updateScenario(scenarioId: string, updates: Partial<Pick<AIAgentScenario, 'title' | 'description' | 'is_active'>>): Promise<AIAgentScenario> {
  const { data, error } = await supabase
    .from('ai_agent_scenarios')
    .update(updates)
    .eq('id', scenarioId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function trainScenario(
  agentId: string,
  message: string,
  businessId: string,
  scenarioId?: string,
  images?: string[],
  action: 'chat' | 'save' = 'chat'
): Promise<{
  success: boolean;
  response?: string;
  scenario_id?: string;
  scenario?: AIAgentScenario;
  rules_created?: AIAgentRule[];
  rules_updated?: AIAgentRule[];
  rules_deleted?: string[];
  tokens_used?: number;
  tokens_remaining?: number;
  error?: string;
  insufficientTokens?: boolean;
}> {
  const { data, error } = await supabase.functions.invoke('ai-agent-scenario', {
    body: {
      agent_id: agentId,
      message,
      scenario_id: scenarioId,
      businessId,
      images,
      action
    }
  });

  if (error) {
    return { success: false, error: error.message };
  }

  return data;
}

// --- Training ---

export async function trainAgent(
  agentId: string,
  message: string,
  businessId: string,
  conversationId?: string,
  images?: string[]
): Promise<{
  success: boolean;
  response?: string;
  conversation_id?: string;
  rules_created?: AIAgentRule[];
  rules_updated?: AIAgentRule[];
  rules_deleted?: string[];
  tokens_used?: number;
  tokens_remaining?: number;
  error?: string;
  insufficientTokens?: boolean;
}> {
  const { data, error } = await supabase.functions.invoke('ai-agent-train', {
    body: {
      agent_id: agentId,
      message,
      conversation_id: conversationId,
      businessId,
      images
    }
  });

  if (error) {
    return { success: false, error: error.message };
  }

  return data;
}

export async function invokeLeadAgentTurn(params: {
  opportunityId: number;
  agentId: string;
  businessId: string;
  message: string;
  threadId?: string | null;
  runMode?:
    | 'user_message'
    | 'inbound_sms'
    | 'scheduled_follow_up'
    | 'heartbeat'
    | 'initial_assign';
}): Promise<{
  success: boolean;
  response?: string;
  thread_id?: string;
  messages?: { role: string; content: string; timestamp?: string }[];
  tokens_used?: number;
  tokens_remaining?: number;
  error?: string;
  insufficientTokens?: boolean;
}> {
  const { data, error } = await supabase.functions.invoke('ai-lead-agent-turn', {
    body: {
      opportunityId: params.opportunityId,
      agentId: params.agentId,
      businessId: params.businessId,
      message: params.message,
      threadId: params.threadId ?? undefined,
      runMode: params.runMode ?? 'user_message',
    },
  });

  if (error) {
    return { success: false, error: error.message };
  }
  return data as any;
}

/** Same edge function as a real lead turn; `simulate: true` — no opportunity, no Twilio/CRM writes. */
export async function invokeLeadAgentSimulateTurn(params: {
  agentId: string;
  businessId: string;
  message: string;
  simulationSessionId?: string | null;
  /** `initial_assign` = same first kickoff as assigning the agent to a real lead (empty message allowed). */
  runMode?: 'inbound_sms' | 'initial_assign';
}): Promise<{
  success: boolean;
  response?: string;
  thread_id?: string;
  simulation_session_id?: string;
  messages?: { role: string; content: string; timestamp?: string }[];
  tokens_used?: number;
  tokens_remaining?: number;
  sms_delivered?: boolean;
  simulate?: boolean;
  error?: string;
  insufficientTokens?: boolean;
}> {
  const runMode = params.runMode ?? 'inbound_sms';
  const { data, error } = await supabase.functions.invoke('ai-lead-agent-turn', {
    body: {
      simulate: true,
      agentId: params.agentId,
      businessId: params.businessId,
      message: params.message,
      simulationSessionId: params.simulationSessionId ?? undefined,
      runMode,
    },
  });

  if (error) {
    return { success: false, error: error.message };
  }
  return data as any;
}
